// Targeted Count - Count Items
import React, { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  ArrowRight,
  Target,
  Search,
  X,
  CheckCircle2,
  AlertCircle,
  Package,
  Filter,
  Save,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { toast } from "sonner";
import { mockWarehouses } from "@/data/mockWarehousesLocations";
import { Checkbox } from "@/components/ui/checkbox";

// Mock stock items data
const mockStockItems = [
  { code: "STK001", description: "Laptop Dell XPS 15", category: "Electronics", supplier: "Supplier A", location: "A01-R01", systemQty: 45, unit: "Each", value: 1200 },
  { code: "STK002", description: "Office Chair Ergonomic", category: "Furniture", supplier: "Supplier B", location: "A01-R02", systemQty: 120, unit: "Each", value: 250 },
  { code: "STK003", description: "A4 Paper Ream (500 sheets)", category: "Stationery", supplier: "Supplier C", location: "B01-R01", systemQty: 350, unit: "Ream", value: 5 },
  { code: "STK004", description: "Steel Rods 10mm x 6m", category: "Raw Materials", supplier: "Supplier A", location: "A02-R01", systemQty: 89, unit: "Piece", value: 45 },
  { code: "STK005", description: "Wireless Mouse Logitech", category: "Electronics", supplier: "Supplier B", location: "B01-R02", systemQty: 200, unit: "Each", value: 25 },
  { code: "STK006", description: "Standing Desk Adjustable", category: "Furniture", supplier: "Supplier A", location: "A01-R03", systemQty: 35, unit: "Each", value: 450 },
  { code: "STK007", description: "Ballpoint Pens Blue (Box of 50)", category: "Stationery", supplier: "Supplier C", location: "B01-R03", systemQty: 180, unit: "Box", value: 8 },
  { code: "STK008", description: "Aluminum Sheets 2mm", category: "Raw Materials", supplier: "Supplier B", location: "A02-R02", systemQty: 67, unit: "Sheet", value: 75 },
  { code: "STK009", description: "Monitor 27\" 4K Dell", category: "Electronics", supplier: "Supplier A", location: "B02-R01", systemQty: 78, unit: "Each", value: 400 },
  { code: "STK010", description: "Filing Cabinet 4-Drawer", category: "Furniture", supplier: "Supplier B", location: "A01-R04", systemQty: 42, unit: "Each", value: 180 },
  { code: "STK011", description: "Whiteboard Markers (Pack of 12)", category: "Stationery", supplier: "Supplier C", location: "B01-R04", systemQty: 95, unit: "Pack", value: 12 },
  { code: "STK012", description: "Copper Wire 2.5mm", category: "Raw Materials", supplier: "Supplier A", location: "A02-R03", systemQty: 450, unit: "Meter", value: 3 },
  { code: "STK013", description: "USB-C Cable 2m", category: "Electronics", supplier: "Supplier B", location: "B02-R02", systemQty: 320, unit: "Each", value: 15 },
  { code: "STK014", description: "Conference Table 8-Seater", category: "Furniture", supplier: "Supplier A", location: "A01-R05", systemQty: 12, unit: "Each", value: 800 },
  { code: "STK015", description: "Sticky Notes 3x3 (Pack of 100)", category: "Stationery", supplier: "Supplier C", location: "B01-R05", systemQty: 240, unit: "Pack", value: 6 },
  { code: "STK016", description: "Mechanical Keyboard RGB", category: "Electronics", supplier: "Supplier A", location: "B02-R03", systemQty: 150, unit: "Each", value: 120 },
  { code: "STK017", description: "Desk Lamp LED", category: "Furniture", supplier: "Supplier B", location: "A01-R06", systemQty: 88, unit: "Each", value: 35 },
  { code: "STK018", description: "Printer Paper A3", category: "Stationery", supplier: "Supplier C", location: "B01-R06", systemQty: 200, unit: "Ream", value: 8 },
  { code: "STK019", description: "Stainless Steel Bolts M10", category: "Raw Materials", supplier: "Supplier A", location: "A02-R04", systemQty: 5000, unit: "Piece", value: 0.5 },
  { code: "STK020", description: "Webcam HD 1080p", category: "Electronics", supplier: "Supplier B", location: "B02-R04", systemQty: 65, unit: "Each", value: 80 },
];

interface CountItem {
  code: string;
  description: string;
  category: string;
  supplier: string;
  location: string;
  systemQty: number;
  unit: string;
  value: number;
  countedQty: string;
  countedBy: string;
  notes: string;
  isCounted: boolean;
}

interface PartialCountPageProps {
  setupData?: {
    formData: {
      date: Date;
      warehouse: string;
      assignedTo: string;
      description: string;
      notes: string;
    };
    filters: {
      category: string;
      supplier: string;
      minValue: string;
      maxValue: string;
      location: string;
      includeZeroQty: boolean;
    };
    prepopulatedItems?: any[]; // Items pre-selected from Stock Items page
  };
}

export function StockTakesPartialCountPage({ setupData }: PartialCountPageProps) {
  const { navigateTo } = useNavigation();
  
  // Initialize count items based on filters or prepopulated items
  const initialItems = useMemo(() => {
    let items = mockStockItems;
    
    // If items were pre-selected from Stock Items page, use those
    if (setupData?.prepopulatedItems && setupData.prepopulatedItems.length > 0) {
      // Map prepopulated items to the count item format
      items = setupData.prepopulatedItems.map((item: any) => ({
        code: item.code,
        description: item.description,
        category: item.category || "Uncategorized",
        supplier: item.supplier || "Unknown",
        location: item.locationCode || item.location || "Unknown",
        systemQty: item.expectedQuantity || item.physicalQuantity || item.quantity || 0,
        unit: item.unitOfMeasure || item.unit || "Each",
        value: item.unitPrice || item.value || 0,
      }));
    } else if (setupData?.filters) {
      // Otherwise, use filters to select items
      const { category, supplier, minValue, maxValue, location, includeZeroQty } = setupData.filters;
      
      // Filter by category
      if (category) {
        items = items.filter(item => item.category === category);
      }
      
      // Filter by supplier
      if (supplier) {
        items = items.filter(item => item.supplier === supplier);
      }
      
      // Filter by value range
      if (minValue) {
        const min = parseFloat(minValue);
        items = items.filter(item => item.value >= min);
      }
      if (maxValue) {
        const max = parseFloat(maxValue);
        items = items.filter(item => item.value <= max);
      }
      
      // Filter by location
      if (location) {
        items = items.filter(item => 
          item.location.toLowerCase().includes(location.toLowerCase())
        );
      }
      
      // Filter by zero quantity
      if (!includeZeroQty) {
        items = items.filter(item => item.systemQty > 0);
      }
    }
    
    // Filter by warehouse if selected
    if (setupData?.formData?.warehouse) {
      const warehouse = mockWarehouses.find(w => w.code === setupData.formData.warehouse);
      if (warehouse) {
        // In a real app, you'd filter by warehouse location
        // For now, we'll keep all items
      }
    }
    
    return items.map(item => ({
      ...item,
      countedQty: "",
      countedBy: "",
      notes: "",
      isCounted: false,
    }));
  }, [setupData]);

  const [countItems, setCountItems] = useState<CountItem[]>(initialItems);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showOnlyVariances, setShowOnlyVariances] = useState(false);

  // Get unique categories from filtered items
  const categories = useMemo(() => {
    return Array.from(new Set(countItems.map(item => item.category)));
  }, [countItems]);

  // Filter items
  const filteredItems = useMemo(() => {
    let filtered = countItems;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        item =>
          item.code.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query) ||
          item.location.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }

    // Status filter
    if (statusFilter === "counted") {
      filtered = filtered.filter(item => item.isCounted);
    } else if (statusFilter === "uncounted") {
      filtered = filtered.filter(item => !item.isCounted);
    }

    // Variance filter
    if (showOnlyVariances) {
      filtered = filtered.filter(item => {
        if (!item.isCounted || !item.countedQty) return false;
        const counted = parseFloat(item.countedQty);
        return !isNaN(counted) && counted !== item.systemQty;
      });
    }

    return filtered;
  }, [countItems, searchQuery, categoryFilter, statusFilter, showOnlyVariances]);

  // Statistics
  const stats = useMemo(() => {
    const total = countItems.length;
    const counted = countItems.filter(item => item.isCounted).length;
    const uncounted = total - counted;
    const variances = countItems.filter(item => {
      if (!item.isCounted || !item.countedQty) return false;
      const countedQty = parseFloat(item.countedQty);
      return !isNaN(countedQty) && countedQty !== item.systemQty;
    }).length;

    return { total, counted, uncounted, variances };
  }, [countItems]);

  const updateCountItem = (code: string, field: keyof CountItem, value: string | boolean) => {
    setCountItems(prev =>
      prev.map(item => {
        if (item.code === code) {
          const updated = { ...item, [field]: value };
          
          // Auto-mark as counted when quantity is entered
          if (field === "countedQty" && value !== "") {
            updated.isCounted = true;
            // Auto-fill countedBy if assignedTo is set
            if (setupData?.formData?.assignedTo && !updated.countedBy) {
              updated.countedBy = setupData.formData.assignedTo;
            }
          }
          
          return updated;
        }
        return item;
      })
    );
  };

  const getVariance = (item: CountItem) => {
    if (!item.countedQty) return null;
    const counted = parseFloat(item.countedQty);
    if (isNaN(counted)) return null;
    return counted - item.systemQty;
  };

  const getVarianceBadge = (variance: number | null) => {
    if (variance === null) return null;
    if (variance === 0) {
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Match</Badge>;
    }
    return (
      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
        {variance > 0 ? `+${variance}` : variance}
      </Badge>
    );
  };

  const handleSaveDraft = () => {
    toast.success("Draft saved successfully");
  };

  const handleContinue = () => {
    const countedItemsCount = countItems.filter(item => item.isCounted).length;
    
    if (countedItemsCount === 0) {
      toast.error("Please count at least one item before continuing");
      return;
    }

    // Navigate to review page
    navigateTo("stock-takes/targeted/review", {
      setupData,
      countedItems: countItems,
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigateTo("stock-takes/targeted/setup")}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Setup
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleSaveDraft}
          className="gap-2"
        >
          <Save className="h-4 w-4" />
          Save Draft
        </Button>
      </div>

      {/* Title Section */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/20">
            <Target className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Count Items</h1>
            <p className="text-muted-foreground">
              {setupData?.formData?.description || "Targeted Count"}
            </p>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Items</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Package className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Counted</p>
                <p className="text-2xl font-bold text-green-600">{stats.counted}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Remaining</p>
                <p className="text-2xl font-bold text-amber-600">{stats.uncounted}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Variances</p>
                <p className="text-2xl font-bold text-red-600">{stats.variances}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search items..."
                  className="pl-9"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setSearchQuery("")}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="categoryFilter">Category</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger id="categoryFilter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(category => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="statusFilter">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger id="statusFilter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Items</SelectItem>
                  <SelectItem value="counted">Counted</SelectItem>
                  <SelectItem value="uncounted">Not Counted</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Options</Label>
              <div className="flex items-center space-x-2 h-10">
                <Checkbox
                  id="showVariances"
                  checked={showOnlyVariances}
                  onCheckedChange={(checked) => setShowOnlyVariances(checked as boolean)}
                />
                <Label
                  htmlFor="showVariances"
                  className="text-sm font-normal cursor-pointer"
                >
                  Show only variances
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Count Table */}
      <Card className="flex-1 flex flex-col">
        <CardHeader>
          <CardTitle>Items to Count</CardTitle>
          <CardDescription>
            Enter the physical count for each item. Items are filtered based on your setup criteria.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Code</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-right">System Qty</TableHead>
                <TableHead className="text-right">Counted Qty</TableHead>
                <TableHead className="text-right">Variance</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No items found matching your filters
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems.map((item) => {
                  const variance = getVariance(item);
                  return (
                    <TableRow key={item.code}>
                      <TableCell className="font-medium">{item.code}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.category}</Badge>
                      </TableCell>
                      <TableCell>{item.location}</TableCell>
                      <TableCell className="text-right">
                        {item.systemQty} {item.unit}
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          value={item.countedQty}
                          onChange={(e) => updateCountItem(item.code, "countedQty", e.target.value)}
                          placeholder="0"
                          className="w-24 text-right ml-auto"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        {getVarianceBadge(variance)}
                      </TableCell>
                      <TableCell>
                        {item.isCounted ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Counted
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t mt-6">
        <Button
          variant="outline"
          onClick={() => navigateTo("stock-takes/targeted/setup")}
        >
          Back to Setup
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSaveDraft}
          >
            Save Draft
          </Button>
          <Button
            onClick={handleContinue}
            disabled={stats.counted === 0}
            className="gap-2"
          >
            Continue to Review
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
